import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import { fetchPostComments, submitComment } from "@/lib/api";

/**
 * A11 — Blog comments component. Public submit, moderated display.
 * Drop into BlogPost.jsx with: <Comments slug={post.slug} />
 */
export default function Comments({ slug }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ author_name: "", author_email: "", text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchPostComments(slug)
      .then((d) => setComments(d.comments || []))
      .catch((e) => console.warn("comments load failed", e))
      .finally(() => setLoading(false));
  }, [slug]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim() || !form.author_email.trim() || form.text.trim().length < 4) {
      toast.error("Please fill all fields. Comments must be at least 4 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await submitComment(slug, form);
      setSubmitted(true);
      setForm({ author_name: "", author_email: "", text: "" });
      toast.success("Thanks — your comment is awaiting moderation.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit comment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="bg-white border-t border-[#e5e7eb] py-16 md:py-20"
      data-testid="comments-section"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-8">
          <MessageSquare size={20} className="text-[#3A7A8C]" />
          <h2 className="text-2xl md:text-3xl font-bold text-[#080808]">
            Discussion {comments.length > 0 && <span className="text-[#5A5F6A] text-lg font-normal">· {comments.length}</span>}
          </h2>
        </div>

        {/* Comments list */}
        {loading ? (
          <div className="text-[#5A5F6A] text-sm">Loading…</div>
        ) : comments.length === 0 ? (
          <p className="text-[#5A5F6A] text-sm mb-8" data-testid="no-comments">
            No comments yet. Start the conversation.
          </p>
        ) : (
          <ul className="space-y-6 mb-10" data-testid="comments-list">
            {comments.map((c) => (
              <li
                key={c.id}
                data-testid={`comment-${c.id}`}
                className="bg-[#F5F5F5] border border-[#e5e7eb] p-5"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <span className="font-semibold text-[#080808]">{c.author_name}</span>
                  <span className="text-xs text-[#5A5F6A]">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                  </span>
                </div>
                <p className="mt-2 text-[#3C4048] text-sm leading-relaxed whitespace-pre-wrap">{c.text}</p>
              </li>
            ))}
          </ul>
        )}

        {/* Submit form */}
        {submitted ? (
          <div
            className="border border-emerald-200 bg-emerald-50 p-5 text-emerald-900 text-sm"
            data-testid="comment-submitted"
          >
            Thanks — your comment is now awaiting moderation. It will appear once approved by our team.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="comment-form">
            <span className="ash-eyebrow">Add to the discussion</span>
            <h3 className="text-xl font-bold text-[#080808] mt-2">Share your thoughts</h3>
            <p className="text-xs text-[#5A5F6A] mb-4">
              Comments are moderated. Your email is never displayed publicly.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-[#5A5F6A] font-semibold mb-2">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={form.author_name}
                  onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                  className="w-full border border-[#e5e7eb] px-3 py-2 bg-white"
                  data-testid="comment-name"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-[#5A5F6A] font-semibold mb-2">
                  Email (not displayed)
                </label>
                <input
                  type="email"
                  required
                  value={form.author_email}
                  onChange={(e) => setForm({ ...form, author_email: e.target.value })}
                  className="w-full border border-[#e5e7eb] px-3 py-2 bg-white"
                  data-testid="comment-email"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[#5A5F6A] font-semibold mb-2">
                Comment
              </label>
              <textarea
                rows={4}
                required
                minLength={4}
                maxLength={2000}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="w-full border border-[#e5e7eb] px-3 py-2 bg-white"
                data-testid="comment-text"
              />
              <div className="text-xs text-[#5A5F6A] mt-1 text-right">{form.text.length}/2000</div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="ash-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="comment-submit"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {submitting ? "Submitting…" : "Submit comment"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
